import React, { CSSProperties, useState } from 'react';
import { DocumentNode } from 'graphql';
import {
  useQuery,
  QueryResult,
  OperationVariables,
  TypedDocumentNode,
} from '@apollo/client';
import {
  Table,
  Text,
  Flex,
  Spinner,
  Button,
  TextField,
  Select,
  Popover,
  IconButton,
  Link,
} from '@radix-ui/themes';
import {
  ChevronLeftIcon,
  ChevronRightIcon,
  ChevronUpIcon,
  ChevronDownIcon,
  MagnifyingGlassIcon,
  MixerHorizontalIcon,
} from '@radix-ui/react-icons';
import {
  InputMaybe,
  SortDirection,
  SortInput,
  FilterInput,
  FilterOperation,
} from '../../types';

type NestedProp<T, K extends string> = K extends keyof T
  ? T[K]
  : K extends `${infer F}.${infer R}`
    ? F extends keyof T
      ? NestedProp<T[F], R>
      : never
    : never;

type EdgeNode<T> = T extends { edges: Array<{ node: infer N }> } ? N : never;

export type FilterType = 'text' | 'number' | 'select';

export type FilterConfig = {
  type: FilterType;
  operation?: FilterOperation;
  options?: Array<{ label: string; value: string }>;
  placeholder?: string;
};

export type Column<T> = {
  header: string | null;
  accessor?: keyof T;
  render?: (props: {
    row: T;
    value: T[keyof T] | undefined;
  }) => React.ReactElement;
  style?: CSSProperties;
  sortable?: boolean;
  sortField?: string;
  filterable?: boolean;
  filterField?: string;
  filterConfig?: FilterConfig;
  disableRowClick?: boolean; // Disable row click for this column (e.g., for action buttons)
};

interface Edge<T> {
  node: T;
  cursor: string;
}

interface PageInfo {
  hasNextPage: boolean;
  hasPreviousPage: boolean;
  startCursor: string | null;
  endCursor: string | null;
}

interface Connection<T> {
  edges: Edge<T>[];
  pageInfo: PageInfo;
  totalCount: number;
}

interface GraphQLTableProps<
  TData extends Record<string, unknown>,
  TVariables extends OperationVariables,
  K extends string,
  T = EdgeNode<NestedProp<TData, K>>,
> extends Table.RootProps {
  query: DocumentNode | TypedDocumentNode<TData, TVariables>;
  variables?: TVariables;
  columns: Column<T>[];
  loadingMessage?: string;
  errorMessage?: string;
  noDataMessage?: string;
  connectionPath: K;
  count?: number;
  removeBottomBorder?: boolean;
  onRowClick?: (row: T) => void;
  getRowHref?: (row: T) => string;
}

function getNestedValue<T, R = unknown>(obj: T, path: string): R {
  return path.split('.').reduce((acc, part) => {
    if (acc && typeof acc === 'object' && part in acc && acc !== null) {
      return acc[part as keyof typeof acc];
    }
    return undefined;
  }, obj as unknown) as R;
}

interface TableContentProps<
  TData extends Record<string, unknown>,
  TVariables extends OperationVariables,
  K extends string,
  T = EdgeNode<NestedProp<TData, K>>,
> extends QueryResult<TData, TVariables> {
  columns: Column<T>[];
  loadingMessage: string;
  errorMessage: string;
  noDataMessage: string;
  connectionPath: K;
  removeBottomBorder?: boolean;
  onRowClick?: (row: T) => void;
  getRowHref?: (row: T) => string;
}

const TableContent = <
  TData extends Record<string, unknown>,
  TVariables extends OperationVariables = OperationVariables,
  K extends string = string,
  T = EdgeNode<NestedProp<TData, K>>,
>({
  loading,
  error,
  data,
  columns,
  loadingMessage,
  errorMessage,
  noDataMessage,
  connectionPath,
  removeBottomBorder = false,
  onRowClick,
  getRowHref,
}: TableContentProps<TData, TVariables, K, T>): React.ReactElement => {
  if (loading) {
    return (
      <Table.Row>
        <Table.Cell colSpan={columns.length}>
          <Flex align="center" justify="center" gap="2" p="5">
            <Spinner size="1" />
            <Text>{loadingMessage}</Text>
          </Flex>
        </Table.Cell>
      </Table.Row>
    );
  }

  if (error) {
    return (
      <Table.Row>
        <Table.Cell colSpan={columns.length}>
          <Text color="red">
            {errorMessage} Error: {error.message}
          </Text>
        </Table.Cell>
      </Table.Row>
    );
  }

  if (!data) {
    return (
      <Table.Row>
        <Table.Cell colSpan={columns.length}>
          <Text>{noDataMessage}</Text>
        </Table.Cell>
      </Table.Row>
    );
  }

  const connection = getNestedValue<
    TData,
    Connection<EdgeNode<NestedProp<TData, K>>>
  >(data, connectionPath) as
    | Connection<EdgeNode<TData[keyof TData]>>
    | undefined;

  if (!connection || connection.edges.length === 0) {
    return (
      <Table.Row>
        <Table.Cell colSpan={columns.length}>
          <Text>{noDataMessage}</Text>
        </Table.Cell>
      </Table.Row>
    );
  }

  return (
    <>
      {connection.edges.map((edge, index) => {
        const isLastRow = index === connection.edges.length - 1;
        const row = edge.node as T;
        const rowHref = getRowHref?.(row);
        const hasRowClick = onRowClick || rowHref;

        const rowContent = (
          <Table.Row
            key={edge.cursor}
            align="center"
            style={{
              borderBottom: removeBottomBorder && isLastRow ? 'none' : undefined,
              cursor: hasRowClick ? 'pointer' : undefined,
            }}
            onClick={
              onRowClick && !rowHref
                ? (e: React.MouseEvent) => {
                    // Don't trigger row click if clicking on a column with disableRowClick
                    const target = e.target as HTMLElement;
                    const cell = target.closest('td');
                    if (cell) {
                      const cellIndex = Array.from(
                        cell.parentElement?.children || [],
                      ).indexOf(cell);
                      if (cellIndex >= 0 && columns[cellIndex]?.disableRowClick) {
                        return;
                      }
                    }
                    onRowClick(row);
                  }
                : undefined
            }
          >
            {columns.map(({ header, accessor, render, style, disableRowClick }, columnIndex) => {
              const value = accessor
                ? getNestedValue(edge.node, accessor as string)
                : null;
              const isActionsColumn =
                columnIndex === columns.length - 1 && header === null;

              return (
                <Table.Cell
                  key={columnIndex}
                  width={isActionsColumn ? 'min-content' : undefined}
                  style={style}
                  onClick={
                    disableRowClick && hasRowClick
                      ? (e: React.MouseEvent) => e.stopPropagation()
                      : undefined
                  }
                >
                  {render ? (
                    render({
                      row: edge.node as T,
                      value: value as T[keyof T] | undefined,
                    })
                  ) : (
                    <Text>{value as React.ReactNode}</Text>
                  )}
                </Table.Cell>
              );
            })}
          </Table.Row>
        );

        if (rowHref) {
          return (
            <Link key={edge.cursor} href={rowHref} style={{ textDecoration: 'none', color: 'inherit' }}>
              {rowContent}
            </Link>
          );
        }

        return rowContent;
      })}
    </>
  );
};

interface PaginationOptions {
  first?: InputMaybe<number>;
  after?: InputMaybe<string>;
  last?: InputMaybe<number>;
  before?: InputMaybe<string>;
  sort?: InputMaybe<SortInput>;
  filters?: InputMaybe<Array<FilterInput>>;
}

const GraphQLTable = <
  TData extends Record<string, unknown>,
  TVariables extends PaginationOptions &
    OperationVariables = OperationVariables,
  K extends string = string,
  T = EdgeNode<NestedProp<TData, K>>,
>(
  props: GraphQLTableProps<TData, TVariables, K, T>,
): React.ReactElement => {
  const {
    query,
    variables,
    columns,
    loadingMessage = 'Loading data...',
    errorMessage = 'An error occurred while fetching data.',
    noDataMessage = 'No data available.',
    connectionPath,
    count = 20,
    removeBottomBorder = false,
    onRowClick,
    getRowHref,
    ...otherProps
  } = props;

  const [sortField, setSortField] = useState<string | null>(null);
  const [sortDirection, setSortDirection] = useState<SortDirection>(
    SortDirection.Desc,
  );
  // Applied filters (used in query)
  const [filters, setFilters] = useState<
    Record<string, { value: string; operation: FilterOperation }>
  >({});
  // Draft filters (local state before applying)
  const [draftFilters, setDraftFilters] = useState<
    Record<string, { value: string; operation: FilterOperation }>
  >({});

  const sortInput: SortInput | undefined =
    sortField !== null
      ? {
          field: sortField,
          direction: sortDirection,
        }
      : undefined;

  // Build filter inputs from state
  const filterInputs: FilterInput[] = Object.entries(filters)
    .filter(([_, filter]) => {
      // Include filters with values, or IS_NULL/IS_NOT_NULL operations
      const isNullCheck = [
        FilterOperation.IsNull,
        FilterOperation.IsNotNull,
      ].includes(filter.operation);
      return filter.value !== '' || isNullCheck;
    })
    .map(([field, filter]) => ({
      field,
      operation: filter.operation,
      value: filter.value,
    }));

  const queryResult = useQuery<TData, TVariables>(query, {
    variables: {
      first: count,
      sort: sortInput,
      filters: filterInputs.length > 0 ? filterInputs : undefined,
      ...variables,
    } as TVariables,
  });

  const connection = queryResult.data
    ? getNestedValue<TData, Connection<EdgeNode<NestedProp<TData, K>>>>(
        queryResult.data,
        connectionPath,
      )
    : undefined;
  const resultsCount = connection ? connection.totalCount : 0;
  const pageInfo = connection ? connection.pageInfo : undefined;

  const handleColumnHeaderClick = (column: Column<T>) => {
    if (!column.sortable || !column.sortField) return;

    if (sortField === column.sortField) {
      // Cycle through: Desc -> Asc -> No Sort -> Desc
      if (sortDirection === SortDirection.Desc) {
        setSortDirection(SortDirection.Asc);
      } else {
        // Clear sorting
        setSortField(null);
      }
    } else {
      // Set new field with descending as default
      setSortField(column.sortField);
      setSortDirection(SortDirection.Desc);
    }
  };

  const getDefaultOperation = (column: Column<T>): FilterOperation => {
    if (column.filterConfig?.operation) {
      return column.filterConfig.operation;
    }
    return column.filterConfig?.type === 'number'
      ? FilterOperation.Eq
      : FilterOperation.ILike;
  };

  const handleDraftFilterChange = (
    field: string,
    value: string,
    operation?: FilterOperation,
  ) => {
    setDraftFilters(
      (prev: Record<string, { value: string; operation: FilterOperation }>) => {
        const column = columns.find((col) => col.filterField === field);
        const currentOperation =
          operation ||
          prev[field]?.operation ||
          filters[field]?.operation ||
          getDefaultOperation(column!);

        return {
          ...prev,
          [field]: { value, operation: currentOperation },
        };
      },
    );
  };

  const handleDraftOperationChange = (
    field: string,
    operation: FilterOperation,
  ) => {
    setDraftFilters(
      (prev: Record<string, { value: string; operation: FilterOperation }>) => {
        const currentValue = prev[field]?.value || filters[field]?.value || '';
        return {
          ...prev,
          [field]: { value: currentValue, operation },
        };
      },
    );
  };

  const applyFilter = (field: string) => {
    const draft = draftFilters[field];
    if (!draft) return;

    // For IS_NULL and IS_NOT_NULL, we don't need a value
    const isNullCheck = [
      FilterOperation.IsNull,
      FilterOperation.IsNotNull,
    ].includes(draft.operation);

    // Only apply if there's a value or it's a null check operation
    if (draft.value !== '' || isNullCheck) {
      setFilters((prev) => ({
        ...prev,
        [field]: draft,
      }));
    }
  };

  const clearFilter = (field: string) => {
    setFilters((prev) => {
      const newFilters = { ...prev };
      delete newFilters[field];
      return newFilters;
    });
    setDraftFilters((prev) => {
      const newDrafts = { ...prev };
      delete newDrafts[field];
      return newDrafts;
    });
  };

  const handlePreviousPageClick = () => {
    if (connection?.pageInfo.startCursor) {
      // @ts-expect-error Known type mismatch with pagination variables
      queryResult.refetch({
        last: count,
        before: connection.pageInfo.startCursor,
        first: undefined,
        after: undefined,
      });
    }
  };

  const handleNextPageClick = () => {
    if (connection?.pageInfo.endCursor) {
      // @ts-expect-error Known type mismatch with pagination variables
      queryResult.refetch({
        first: count,
        after: connection.pageInfo.endCursor,
        last: undefined,
        before: undefined,
      });
    }
  };

  const getOperationLabel = (op: FilterOperation): string => {
    const labels: Record<FilterOperation, string> = {
      [FilterOperation.Eq]: 'Equals',
      [FilterOperation.NotEq]: 'Not Equals',
      [FilterOperation.Gt]: 'Greater Than',
      [FilterOperation.Gte]: 'Greater Than or Equal',
      [FilterOperation.Lt]: 'Less Than',
      [FilterOperation.Lte]: 'Less Than or Equal',
      [FilterOperation.Like]: 'Like (case-sensitive)',
      [FilterOperation.ILike]: 'Contains (case-insensitive)',
      [FilterOperation.IsNull]: 'Is Null',
      [FilterOperation.IsNotNull]: 'Is Not Null',
    };
    return labels[op] || op;
  };

  const getAvailableOperations = (
    filterType: FilterType,
  ): FilterOperation[] => {
    if (filterType === 'number') {
      return [
        FilterOperation.Eq,
        FilterOperation.NotEq,
        FilterOperation.Gt,
        FilterOperation.Gte,
        FilterOperation.Lt,
        FilterOperation.Lte,
        FilterOperation.IsNull,
        FilterOperation.IsNotNull,
      ];
    }
    // text and select types
    return [
      FilterOperation.Eq,
      FilterOperation.NotEq,
      FilterOperation.ILike,
      FilterOperation.Like,
      FilterOperation.IsNull,
      FilterOperation.IsNotNull,
    ];
  };

  const renderFilterInput = (column: Column<T>) => {
    if (!column.filterable || !column.filterField) {
      return null;
    }

    const filterConfig = column.filterConfig || { type: 'text' as FilterType };
    const appliedFilter = filters[column.filterField];
    const draftFilter = draftFilters[column.filterField];
    const filterValue = draftFilter?.value ?? appliedFilter?.value ?? '';
    const filterOperation =
      draftFilter?.operation ??
      appliedFilter?.operation ??
      getDefaultOperation(column);
    const availableOperations = getAvailableOperations(filterConfig.type);
    const needsValue = ![
      FilterOperation.IsNull,
      FilterOperation.IsNotNull,
    ].includes(filterOperation);

    const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
      if (e.key === 'Enter') {
        applyFilter(column.filterField!);
      }
    };

    if (filterConfig.type === 'select' && filterConfig.options) {
      return (
        <>
          <Select.Root
            value={filterOperation}
            onValueChange={(value: string) => {
              handleDraftOperationChange(
                column.filterField!,
                value as FilterOperation,
              );
              // Auto-apply when operation changes
              setTimeout(() => applyFilter(column.filterField!), 0);
            }}
          >
            <Select.Trigger placeholder="Operation..." />
            <Select.Content>
              {availableOperations.map((op) => (
                <Select.Item key={op} value={op}>
                  {getOperationLabel(op)}
                </Select.Item>
              ))}
            </Select.Content>
          </Select.Root>
          {needsValue && (
            <Select.Root
              value={filterValue}
              onValueChange={(value: string) => {
                handleDraftFilterChange(column.filterField!, value);
                // Auto-apply when select value changes
                setTimeout(() => applyFilter(column.filterField!), 0);
              }}
            >
              <Select.Trigger
                placeholder={filterConfig.placeholder || 'Filter...'}
                style={{ width: '100%' }}
              />
              <Select.Content>
                <Select.Item value="">All</Select.Item>
                {filterConfig.options.map((option) => (
                  <Select.Item key={option.value} value={option.value}>
                    {option.label}
                  </Select.Item>
                ))}
              </Select.Content>
            </Select.Root>
          )}
        </>
      );
    }

    return (
      <>
        <Select.Root
          value={filterOperation}
          onValueChange={(value: string) => {
            handleDraftOperationChange(
              column.filterField!,
              value as FilterOperation,
            );
            // Auto-apply when operation changes
            setTimeout(() => applyFilter(column.filterField!), 0);
          }}
        >
          <Select.Trigger placeholder="Operation..." />
          <Select.Content>
            {availableOperations.map((op) => (
              <Select.Item key={op} value={op}>
                {getOperationLabel(op)}
              </Select.Item>
            ))}
          </Select.Content>
        </Select.Root>
        {needsValue && (
          <TextField.Root
            placeholder={filterConfig.placeholder || 'Filter value...'}
            value={filterValue}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
              handleDraftFilterChange(column.filterField!, e.target.value)
            }
            onKeyDown={handleKeyDown}
            type={filterConfig.type === 'number' ? 'number' : 'text'}
            size="2"
          >
            <TextField.Slot>
              <MagnifyingGlassIcon height="14" width="14" />
            </TextField.Slot>
          </TextField.Root>
        )}
        <Button
          size="2"
          onClick={() => applyFilter(column.filterField!)}
          disabled={
            !draftFilter ||
            (draftFilter.value === '' &&
              ![FilterOperation.IsNull, FilterOperation.IsNotNull].includes(
                draftFilter.operation,
              ))
          }
        >
          Apply Filter
        </Button>
      </>
    );
  };

  const renderFilterButton = (column: Column<T>) => {
    if (!column.filterable || !column.filterField) {
      return null;
    }

    const filter = filters[column.filterField];
    const hasActiveFilter =
      filter &&
      (filter.value !== '' ||
        [FilterOperation.IsNull, FilterOperation.IsNotNull].includes(
          filter.operation,
        ));

    return (
      <Popover.Root>
        <Popover.Trigger>
          <IconButton
            size="1"
            variant="ghost"
            color={hasActiveFilter ? 'blue' : 'gray'}
            style={{ marginLeft: '4px' }}
          >
            <MixerHorizontalIcon />
          </IconButton>
        </Popover.Trigger>
        <Popover.Content style={{ width: '300px' }}>
          <Flex direction="column" gap="3">
            <Text size="2" weight="bold">
              Filter {column.header}
            </Text>
            {renderFilterInput(column)}
            {hasActiveFilter && (
              <Button
                size="1"
                variant="soft"
                color="gray"
                onClick={() => clearFilter(column.filterField!)}
              >
                Clear Filter
              </Button>
            )}
          </Flex>
        </Popover.Content>
      </Popover.Root>
    );
  };

  return (
    <>
      <Table.Root {...otherProps}>
        <Table.Header>
          <Table.Row>
            {columns.map((column, index) => {
              const isSorted = column.sortField === sortField;
              const isSortable = column.sortable && column.sortField;
              const isFilterable = column.filterable && column.filterField;

              return (
                <Table.ColumnHeaderCell
                  key={index}
                  style={{
                    userSelect: 'none',
                  }}
                >
                  <Flex align="center" gap="1" justify="between">
                    <Flex
                      align="center"
                      gap="1"
                      style={{
                        cursor: isSortable ? 'pointer' : 'default',
                      }}
                      onClick={() => handleColumnHeaderClick(column)}
                    >
                      {column.header}
                      {isSortable && isSorted && (
                        <>
                          {sortDirection === SortDirection.Asc ? (
                            <ChevronUpIcon />
                          ) : (
                            <ChevronDownIcon />
                          )}
                        </>
                      )}
                    </Flex>
                    {isFilterable && renderFilterButton(column)}
                  </Flex>
                </Table.ColumnHeaderCell>
              );
            })}
          </Table.Row>
        </Table.Header>
        <Table.Body>
          <TableContent
            {...queryResult}
            columns={columns}
            loadingMessage={loadingMessage}
            errorMessage={errorMessage}
            noDataMessage={noDataMessage}
            connectionPath={connectionPath}
            removeBottomBorder={removeBottomBorder && !pageInfo?.hasNextPage && !pageInfo?.hasPreviousPage}
            onRowClick={onRowClick}
            getRowHref={getRowHref}
          />
        </Table.Body>
      </Table.Root>
      {queryResult && queryResult.data && resultsCount > 0 && (
        <Flex p="1" gap="2" flexGrow="1" justify="between" align="center">
          <Flex m="2" align="center">
            <Button
              variant="ghost"
              onClick={handlePreviousPageClick}
              disabled={!pageInfo?.hasPreviousPage}
            >
              <ChevronLeftIcon />
              Previous
            </Button>
          </Flex>
          <Flex m="2" align="center">
            <Text size="2" weight="bold">
              {resultsCount} Results
            </Text>
          </Flex>
          <Flex m="2" align="center">
            <Button
              variant="ghost"
              onClick={handleNextPageClick}
              disabled={!pageInfo?.hasNextPage}
            >
              Next
              <ChevronRightIcon />
            </Button>
          </Flex>
        </Flex>
      )}
    </>
  );
};

export default GraphQLTable;
